import React, { FC, useCallback, useMemo } from 'react';

import { Divider, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { Box, Stack } from '@mui/system';

import {
  AdminAllowanceNames,
  AllowancesNames,
  ContractTypes,
  convertToDecimals,
  currency,
  decimalMaxLength,
  getLongTermContractAttributes,
} from '@pharmaplan/common';
import { getDaysDistribution } from '@pharmaplan/common/helpers/utils';

import { useAppSelector } from '../../../../../hooks/useAppSelector';
import CustomTextField from '../../../../common/CustomTextField';
import { Constants } from '../../../../../helpers/Constants';
import strings from '../../../../../localization';

import classes from './styles';

interface ILongTermHourlyRates {
  formik: FormikProps<any>;
  customWeekHours?: number;
  customWeekendHours?: number;
  CustomContent?: React.ReactNode;
  bookedWeekdayCost?: number;
  bookedWeekendCost?: number;
}

const LongTermHourlyRates: FC<ILongTermHourlyRates> = ({
  formik,
  customWeekHours,
  customWeekendHours,
  CustomContent,
  bookedWeekdayCost,
  bookedWeekendCost,
}) => {
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { startDate, endDate, shiftContractAttributes } = longTermAttributes ?? {};

  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
  } = formik.values ?? {};

  const { weekdayHours, weekendHours } = useMemo(() => {
    if (
      (customWeekHours || customWeekHours === 0)
      && (customWeekendHours || customWeekendHours === 0)
    ) {
      return {
        weekdayHours: customWeekHours,
        weekendHours: customWeekendHours,
      };
    }
    return getDaysDistribution(shiftContractAttributes);
  }, [customWeekHours, customWeekendHours, shiftContractAttributes]);

  const weekdayTotalRate = weekDayPaidHourlyRate * weekdayHours - (bookedWeekdayCost ?? 0);
  const weekendTotalRate = weekendPaidHourlyRate * weekendHours - (bookedWeekendCost ?? 0);
  const pharmacyWeekdayTotalRate = pharmacyWeekDayHourlyRate * weekdayHours;
  const pharmacyWeekendTotalRate = pharmacyWeekendHourlyRate * weekendHours;
  const totalBookedCost = (bookedWeekdayCost ?? 0) + (bookedWeekendCost ?? 0);

  const professionalTotal = `${currency}${convertToDecimals(
    weekdayTotalRate + weekendTotalRate + totalBookedCost,
  )} + Tx ${strings.and} ${strings.otherFees}`;

  const pharmacyTotal = `${currency}${convertToDecimals(
    pharmacyWeekdayTotalRate + pharmacyWeekendTotalRate,
  )}`;

  const showCalculation = startDate && endDate;
  const showWeekday = weekDayPaidHourlyRate && weekdayHours;
  const showWeekend = weekendPaidHourlyRate && weekendHours;

  const showPharmacyWeekday = pharmacyWeekDayHourlyRate && weekdayHours;
  const showPharmacyWeekend = pharmacyWeekendHourlyRate && weekendHours;

  const fieldConfig = [
    {
      label: strings.formatString(strings.userWeekdayRate, strings.pharmacist),
      name: AllowancesNames.weekDayPaidHourlyRate,
      id: AllowancesNames.weekDayPaidHourlyRate,
      disabled: !weekdayHours,
    },
    {
      label: strings.formatString(strings.userWeekendRate, strings.pharmacist),
      name: AllowancesNames.weekendPaidHourlyRate,
      id: AllowancesNames.weekendPaidHourlyRate,
      disabled: !weekendHours,
    },

    {
      label: strings.formatString(strings.userWeekdayRate, strings.pharmacy),
      name: AdminAllowanceNames.pharmacyWeekDayHourlyRate,
      id: AdminAllowanceNames.pharmacyWeekDayHourlyRate,
      disabled: !weekdayHours,
    },
    {
      label: strings.formatString(strings.userWeekendRate, strings.pharmacy),
      name: AdminAllowanceNames.pharmacyWeekendHourlyRate,
      id: AdminAllowanceNames.pharmacyWeekendHourlyRate,
      disabled: !weekendHours,
    },
  ];

  const calculationConfig = [
    {
      label: strings.formatString(strings.userWeekday, strings.pharmacist),
      value: `${currency}${weekDayPaidHourlyRate} X ${weekdayHours} = ${currency}${convertToDecimals(
        weekdayTotalRate,
      )}`,
      show: !!showWeekday,
    },
    {
      label: strings.formatString(strings.userWeekend, strings.pharmacist),
      value: `${currency}${weekendPaidHourlyRate} X ${weekendHours} = ${currency}${convertToDecimals(
        weekendTotalRate,
      )}`,
      show: !!showWeekend,
    },
  ];

  const pharmacyCalculationConfig = [
    {
      label: strings.formatString(strings.userWeekday, strings.pharmacy),
      value: `${currency}${pharmacyWeekDayHourlyRate} X ${weekdayHours} = ${currency}${convertToDecimals(
        pharmacyWeekdayTotalRate,
      )}`,
      show: !!showPharmacyWeekday,
    },
    {
      label: strings.formatString(strings.userWeekend, strings.pharmacy),
      value: `${currency}${pharmacyWeekendHourlyRate} X ${weekendHours} = ${currency}${convertToDecimals(
        pharmacyWeekendTotalRate,
      )}`,
      show: !!showPharmacyWeekend,
    },
  ];

  const renderConfig = useCallback((config: typeof calculationConfig) =>
    (
      config.map((item) => {
        const { label, value, show } = item ?? {};

        return (
          show && (
          <Box sx={classes.weekContainer}>
            <Typography sx={classes.label}>{label}</Typography>
            <Typography sx={classes.rateValue}>{value}</Typography>
          </Box>
          )
        );
      })
    ), []);

  if (formik.values.contractType === ContractTypes.shortTerm) return null;

  return (
    <Stack>
      <Typography sx={classes.title}>{strings.rateAndAllowances}</Typography>

      {fieldConfig.map((item) => {
        const { label, name, id, disabled } = item;
        return (
          <CustomTextField
            label={label as string}
            name={name}
            formik={formik}
            disabled={disabled}
            placeholder={Constants.allowancePlaceholder}
            suffix={Constants.priceSuffix}
            maxLength={decimalMaxLength}
            customClass={classes.hourlyRate}
            id={id}
            horizontal
          />
        );
      })}

      {CustomContent}

      {showCalculation && (
        <Box sx={classes.calculationContainer}>
          <Typography sx={classes.title}>
            {strings.estimatedShiftCost}
          </Typography>

          {renderConfig(calculationConfig)}

          {!!totalBookedCost && (
            <Box sx={classes.weekContainer}>
              <Typography sx={classes.label}>
                {strings.bookedContractValue}
              </Typography>
              <Typography sx={classes.rateValue}>
                {`${currency}${convertToDecimals(totalBookedCost)}`}
              </Typography>
            </Box>
          )}
          <Divider sx={classes.divider} />
          <Box sx={classes.weekContainer}>
            <Typography sx={classes.label}>{strings.total}</Typography>
            <Typography sx={classes.rateValue}>{professionalTotal}</Typography>
          </Box>
        </Box>
      )}

      {showCalculation && (
        <Box sx={classes.calculationContainer}>

          {renderConfig(pharmacyCalculationConfig)}

          <Divider sx={classes.divider} />
          <Box sx={classes.weekContainer}>
            <Typography sx={classes.label}>{strings.total}</Typography>
            <Typography sx={classes.rateValue}>{pharmacyTotal}</Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
export default LongTermHourlyRates;
